import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../utils/errors';
import api from '../api';
import { safeLocal } from '../utils/safeStorage';
import { FONT_FAMILY } from '../constants/font';

export default function Login() {
  const { login, mfaChallenge } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(() => {
    const saved = safeLocal.getItem('keep_signed_in_pref');
    return saved === null ? true : saved === '1';
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // MFA state: si el backend pide codigo 6 digitos, cambiamos el form
  const [mfaChallengeToken, setMfaChallengeToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  // Forgot password modal state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotError, setForgotError] = useState('');

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMsg('');
    setForgotLoading(true);
    try {
      const res = await api.post('/api/auth/forgot-password', {
        email: forgotEmail.trim(),
      });
      setForgotMsg(res.data.message || 'Solicitud registrada.');
      setForgotEmail('');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setForgotError(detail || 'Error al enviar solicitud.');
    } finally {
      setForgotLoading(false);
    }
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      try { safeLocal.setItem('keep_signed_in_pref', keepSignedIn ? '1' : '0'); } catch {}
      const result = await login(email, password, keepSignedIn);
      // Si el backend pidio MFA, cambiar a pantalla de codigo
      if (result && result.mfaRequired) {
        setMfaChallengeToken(result.challengeToken);
      }
    } catch (err) {
      setError(formatApiError(err, 'No se pudo iniciar sesión.'));
    }
    setLoading(false);
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const code = (mfaCode || '').trim();
      if (!/^\d{6}$/.test(code)) {
        throw new Error('El codigo debe ser 6 digitos');
      }
      await mfaChallenge(mfaChallengeToken, code, keepSignedIn);
    } catch (err) {
      setError(formatApiError(err, 'Codigo incorrecto o expirado'));
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const cancelMfa = () => {
    setMfaChallengeToken(null);
    setMfaCode('');
    setError('');
  };

  /* ===== MFA CHALLENGE SCREEN (2do paso si el user tiene MFA) ===== */
  if (mfaChallengeToken) {
    const mfaStyles = {
      wrap: {
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #1A1A1A 0%, #2A1A0A 100%)',
        fontFamily: FONT_FAMILY, padding: 20,
      },
      card: {
        background: '#fff', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)', textAlign: 'center',
      },
      icon: { fontSize: 48, marginBottom: 12 },
      title: { fontSize: 22, fontWeight: 800, color: '#1A1A1A', margin: '0 0 6px', letterSpacing: '-0.02em' },
      sub: { fontSize: 13, color: '#6B7280', margin: '0 0 24px', lineHeight: 1.5 },
      codeInput: {
        width: '100%', padding: '14px 16px', fontSize: 28, letterSpacing: '0.4em',
        textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
        border: '2px solid #E5E7EB', borderRadius: 10, outline: 'none',
        fontFamily: FONT_FAMILY,
      },
      btn: {
        width: '100%', padding: '13px 16px', borderRadius: 10, border: 'none',
        background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #FF6B00, #EA580C)',
        color: '#fff', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
        marginTop: 14, letterSpacing: '0.02em',
      },
      cancel: {
        marginTop: 12, background: 'transparent', border: 'none', color: '#6B7280',
        fontSize: 12, cursor: 'pointer', padding: 6, textDecoration: 'underline',
      },
    };
    return (
      <div style={mfaStyles.wrap}>
        <form onSubmit={handleMfaSubmit} style={mfaStyles.card}>
          <div style={mfaStyles.icon}>🔐</div>
          <h2 style={mfaStyles.title}>Verificación en 2 pasos</h2>
          <p style={mfaStyles.sub}>
            Abre Google Authenticator / Authy / 1Password y escribe el código de 6 dígitos para tu cuenta.
          </p>
          {error && <div style={{ ...styles.error, marginBottom: 14 }}>{error}</div>}
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
            maxLength={6}
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={mfaStyles.codeInput}
            placeholder="······"
          />
          <button type="submit" disabled={loading || mfaCode.length !== 6} style={mfaStyles.btn}>
            {loading ? 'Verificando...' : 'Continuar'}
          </button>
          <button type="button" onClick={cancelMfa} style={mfaStyles.cancel}>
            Cancelar y volver al login
          </button>
        </form>
      </div>
    );
  }

  /* ===== MOBILE LAYOUT ===== */
  if (isMobile) {
    return (
      <div style={mobile.container}>
        {/* Compact header */}
        <div style={mobile.header}>
          <div style={mobile.logoRow}>
            <div style={mobile.logoMark}>
              <span style={mobile.logoR}>W</span>
            </div>
            <div>
              <h1 style={mobile.brandTitle}>RAPPI.2</h1>
              <p style={mobile.brandSub}>WH RAPPI</p>
            </div>
          </div>
        </div>

        {/* Form area */}
        <div style={mobile.body}>
          <h2 style={mobile.formTitle}>Iniciar Sesion</h2>
          <p style={mobile.formSub}>Ingresa tus credenciales</p>

          <form onSubmit={handleSubmit} style={mobile.form}>
            {error && <div style={styles.error}>{error}</div>}
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={mobile.input}
                placeholder="tu@email.com"
                required
                autoComplete="email"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ ...mobile.input, paddingRight: 44 }}
                  placeholder="Tu contrasena"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    padding: 6, fontSize: 16, lineHeight: 1, color: '#6B7280',
                  }}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={keepSignedIn}
                onChange={e => setKeepSignedIn(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              Mantener sesión iniciada
            </label>
            <button type="submit" style={mobile.button} disabled={loading}>
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              style={{
                marginTop: 8, padding: '8px 12px', background: 'none', border: 'none',
                color: '#6B7280', fontSize: 13, cursor: 'pointer', textAlign: 'center',
                textDecoration: 'underline', fontFamily: FONT_FAMILY,
              }}
            >
              ¿Olvidé mi contraseña?
            </button>
          </form>

        </div>

        <p style={mobile.footer}>WHarq - 2026</p>

        {showForgot && (
          <ForgotPasswordModal
            email={forgotEmail}
            setEmail={setForgotEmail}
            loading={forgotLoading}
            msg={forgotMsg}
            error={forgotError}
            onSubmit={handleForgotSubmit}
            onClose={() => { setShowForgot(false); setForgotMsg(''); setForgotError(''); setForgotEmail(''); }}
          />
        )}
      </div>
    );
  }

  /* ===== DESKTOP LAYOUT (unchanged) ===== */
  return (
    <div style={styles.container}>
      {/* Left panel - branding */}
      <div style={styles.leftPanel}>
        <div>
          <div style={styles.logoMark}>
            <span style={styles.logoR}>W</span>
          </div>
          <h1 style={styles.brandTitle}>RAPPI.2</h1>
          <p style={styles.brandSubtitle}>WH RAPPI</p>
          <div style={styles.brandDivider} />
          <p style={styles.brandFooter}>Plataforma de gestion integral para la implementacion de material POP</p>
        </div>
        <p style={styles.leftFooter}>WHarq - 2026</p>
      </div>

      {/* Right panel - login form */}
      <div style={styles.rightPanel}>
        <div style={styles.formContainer}>
          <h2 style={styles.formTitle}>Bienvenido</h2>
          <p style={styles.formSubtitle}>Ingresa tus credenciales para continuar</p>

          <form onSubmit={handleSubmit} style={styles.form}>
            {error && <div style={styles.error}>{error}</div>}
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={styles.input}
                placeholder="tu@email.com"
                required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ ...styles.input, paddingRight: 44 }}
                  placeholder="Tu contrasena"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    padding: 6, fontSize: 16, lineHeight: 1, color: '#6B7280',
                  }}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={keepSignedIn}
                onChange={e => setKeepSignedIn(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              Mantener sesión iniciada
            </label>
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              style={{
                marginTop: 8, padding: '8px 12px', background: 'none', border: 'none',
                color: '#6B7280', fontSize: 13, cursor: 'pointer', textAlign: 'center',
                textDecoration: 'underline', fontFamily: FONT_FAMILY,
              }}
            >
              ¿Olvidé mi contraseña?
            </button>
          </form>

        </div>
      </div>

      {showForgot && (
        <ForgotPasswordModal
          email={forgotEmail}
          setEmail={setForgotEmail}
          loading={forgotLoading}
          msg={forgotMsg}
          error={forgotError}
          onSubmit={handleForgotSubmit}
          onClose={() => { setShowForgot(false); setForgotMsg(''); setForgotError(''); setForgotEmail(''); }}
        />
      )}
    </div>
  );
}

function ForgotPasswordModal({ email, setEmail, loading, msg, error, onSubmit, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, padding: 28,
        maxWidth: 420, width: '100%',
        fontFamily: FONT_FAMILY,
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
      }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1A1A1A' }}>
          ¿Olvidaste tu contraseña?
        </h2>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 8, marginBottom: 18, lineHeight: 1.5 }}>
          Escribe tu email registrado y te enviaremos un correo con un enlace para crear una nueva contraseña. Revisa tu bandeja de entrada y la carpeta de spam.
        </p>

        {msg ? (
          <>
            <div style={{
              padding: '14px 16px', background: '#ECFDF5', border: '1px solid #10B981',
              borderRadius: 8, fontSize: 13, color: '#065F46', lineHeight: 1.5,
            }}>
              ✓ {msg}
            </div>
            <button onClick={onClose} style={{
              marginTop: 16, width: '100%', padding: '12px',
              background: '#1A1A1A', color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              fontFamily: FONT_FAMILY,
            }}>
              Cerrar
            </button>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Email
            </label>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
              style={{
                width: '100%', padding: '12px 14px', marginTop: 6,
                border: '1.5px solid #E5E7EB', borderRadius: 8,
                fontSize: 14, fontFamily: FONT_FAMILY,
                boxSizing: 'border-box',
              }}
            />
            {error && (
              <div style={{
                marginTop: 12, padding: '10px 12px', background: '#FEF2F2',
                border: '1px solid #EF4444', borderRadius: 6,
                fontSize: 12, color: '#991B1B',
              }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button type="button" onClick={onClose} disabled={loading} style={{
                flex: 1, padding: '12px', background: '#fff', color: '#374151',
                border: '1.5px solid #E5E7EB', borderRadius: 8,
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
                fontFamily: FONT_FAMILY,
              }}>
                Cancelar
              </button>
              <button type="submit" disabled={loading || !email.trim()} style={{
                flex: 1, padding: '12px',
                background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #FF6B00, #EA580C)',
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: FONT_FAMILY,
              }}>
                {loading ? 'Enviando...' : 'Enviar solicitud'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ===== MOBILE STYLES ===== */
const mobile = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: FONT_FAMILY,
    background: '#F9FAFB',
  },
  header: {
    background: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)',
    padding: '24px 20px',
    borderRadius: '0 0 20px 20px',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  logoMark: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #FF6B00, #FF8C33)',
    boxShadow: '0 4px 14px rgba(255, 107, 0, 0.35)',
    flexShrink: 0,
  },
  logoR: { color: '#fff', fontSize: 22, fontWeight: 800 },
  brandTitle: {
    margin: 0, fontSize: 22, color: '#FFFFFF', fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  brandSub: {
    margin: '2px 0 0', fontSize: 12, color: '#9CA3AF',
  },
  body: {
    flex: 1,
    padding: '28px 24px 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  formTitle: {
    margin: 0, fontSize: 24, fontWeight: 700, color: '#1A1A1A',
    letterSpacing: '-0.02em',
  },
  formSub: {
    margin: '6px 0 24px', fontSize: 14, color: '#6B7280',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 18 },
  input: {
    padding: '14px 16px',
    border: '1.5px solid #E5E7EB',
    borderRadius: 12,
    fontSize: 16,            // 16px prevents iOS zoom on focus
    fontFamily: FONT_FAMILY,
    color: '#1A1A1A',
    background: '#FFFFFF',
    WebkitAppearance: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  button: {
    padding: '16px',
    background: 'linear-gradient(135deg, #FF6B00, #FF8C33)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    fontFamily: FONT_FAMILY,
    marginTop: 6,
    boxShadow: '0 4px 14px rgba(255, 107, 0, 0.3)',
    cursor: 'pointer',
    WebkitAppearance: 'none',
  },
  demoBox: {
    marginTop: 28,
    padding: '16px',
    background: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #E5E7EB',
  },
  demoTitle: {
    margin: '0 0 10px', fontSize: 11, fontWeight: 600, color: '#9CA3AF',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  demoGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '6px 10px',
    alignItems: 'center',
  },
  demoEmail: { fontSize: 13, color: '#374151' },
  demoPass: { margin: '10px 0 0', fontSize: 12, color: '#9CA3AF' },
  footer: {
    textAlign: 'center', fontSize: 11, color: '#9CA3AF',
    padding: '12px 0 20px', margin: 0,
  },
};

/* ===== DESKTOP STYLES ===== */
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    fontFamily: FONT_FAMILY,
  },
  leftPanel: {
    flex: '0 0 480px',
    background: 'linear-gradient(160deg, #1A1A1A 0%, #2D2D2D 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '60px 56px',
  },
  logoMark: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #FF6B00, #FF8C33)',
    marginBottom: 24,
    boxShadow: '0 8px 24px rgba(255, 107, 0, 0.35)',
  },
  logoR: { color: '#fff', fontSize: 30, fontWeight: 800 },
  brandTitle: {
    margin: 0, fontSize: 36, color: '#FFFFFF', fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  brandSubtitle: {
    margin: '8px 0 0', color: '#9CA3AF', fontSize: 16,
  },
  brandDivider: {
    width: 48, height: 3, background: '#FF6B00', borderRadius: 2,
    margin: '32px 0',
  },
  statsRow: {
    display: 'flex', gap: 32, marginBottom: 32,
  },
  statItem: {
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  statValue: {
    fontSize: 28, fontWeight: 700, color: '#FFFFFF',
    fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em',
  },
  statLabel: {
    fontSize: 12, color: '#6B7280', textTransform: 'uppercase',
    letterSpacing: '0.06em', fontWeight: 500,
  },
  brandFooter: {
    fontSize: 14, color: '#6B7280', lineHeight: 1.6, maxWidth: 340, margin: 0,
  },
  leftFooter: {
    fontSize: 12, color: '#4B5563', margin: 0,
  },

  rightPanel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F9FAFB',
    padding: '40px',
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
  },
  formTitle: {
    margin: 0, fontSize: 28, fontWeight: 700, color: '#1A1A1A',
    letterSpacing: '-0.02em',
  },
  formSubtitle: {
    margin: '8px 0 32px', fontSize: 15, color: '#6B7280',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: {
    fontSize: 12, fontWeight: 600, color: '#374151',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  input: {
    padding: '13px 16px',
    border: '1.5px solid #E5E7EB',
    borderRadius: 10,
    fontSize: 15,
    fontFamily: FONT_FAMILY,
    color: '#1A1A1A',
    background: '#FFFFFF',
  },
  button: {
    padding: '14px',
    background: 'linear-gradient(135deg, #FF6B00, #FF8C33)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    fontFamily: FONT_FAMILY,
    marginTop: 4,
    boxShadow: '0 4px 14px rgba(255, 107, 0, 0.25)',
    cursor: 'pointer',
  },
  error: {
    background: '#FEF2F2',
    color: '#DC2626',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 14,
    textAlign: 'center',
    border: '1px solid #FECACA',
  },
  demoAccounts: {
    marginTop: 32,
    padding: '20px',
    background: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #E5E7EB',
  },
  demoTitle: {
    margin: '0 0 12px', fontSize: 11, fontWeight: 600, color: '#9CA3AF',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  demoList: {
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  demoItem: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  demoBadge: {
    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    minWidth: 50, textAlign: 'center',
  },
  demoEmail: {
    fontSize: 13, color: '#374151',
  },
  demoPass: {
    margin: '10px 0 0', fontSize: 12, color: '#9CA3AF',
  },
};
